(function () {
  'use strict';

  const controllers = new WeakMap();

  function toInventory(value) {
    const number = Number.parseInt(String(value), 10);
    return Number.isFinite(number) ? Math.max(0, number) : 0;
  }

  function normalizeQuantity(input, maximum) {
    const max = toInventory(maximum);
    const numericValue = Number(input && input.value);
    const integerValue = Number.isFinite(numericValue) ? Math.floor(numericValue) : 1;
    const quantity = max > 0 ? Math.min(max, Math.max(1, integerValue)) : 1;

    if (input) {
      input.max = String(Math.max(1, max));
      input.value = String(quantity);
    }

    return quantity;
  }

  function selectedVariant(form) {
    const radios = Array.from(form.querySelectorAll('[data-frozen-variant-radio]'));
    if (radios.length > 0) {
      return radios.find((radio) => radio.checked && !radio.disabled && radio.dataset.buyable === 'true') || null;
    }

    const singleVariant = form.querySelector('[data-frozen-single-variant]');
    return singleVariant && singleVariant.dataset.buyable === 'true' ? singleVariant : null;
  }

  function updateQuantityControls(form, variant) {
    const input = form.querySelector('[data-frozen-quantity-input]');
    const minusButton = form.querySelector('[data-frozen-quantity-minus]');
    const plusButton = form.querySelector('[data-frozen-quantity-plus]');
    const maximum = variant ? toInventory(variant.dataset.inventory) : 0;

    if (!input) return 1;

    input.max = String(Math.max(1, maximum));
    input.disabled = maximum < 1;
    const quantity = normalizeQuantity(input, maximum);

    if (minusButton) minusButton.disabled = maximum < 1 || quantity <= 1;
    if (plusButton) plusButton.disabled = maximum < 1 || quantity >= maximum;

    return quantity;
  }

  function updateVariantState(card, form) {
    const variant = selectedVariant(form);
    const radios = Array.from(form.querySelectorAll('[data-frozen-variant-radio]'));
    const price = form.querySelector('[data-frozen-price]');
    const addButton = form.querySelector('[data-frozen-add]');

    radios.forEach((radio) => {
      const label = radio.nextElementSibling;
      if (label && label.hasAttribute('data-frozen-variant-label')) {
        label.classList.toggle('is-active', radio === variant);
      }
    });

    if (price && variant && variant.dataset.price) {
      price.textContent = variant.dataset.price;
    }

    updateQuantityControls(form, variant);
    if (addButton) addButton.disabled = !variant;

    if (variant) {
      const nutritionRoot = form.querySelector('[data-sm-nutrition-root]');
      if (nutritionRoot) nutritionRoot.dataset.selectedVariantId = variant.dataset.variantId;

      document.dispatchEvent(new CustomEvent('simplymeals:nutrition:variant-change', {
        detail: {
          productId: card.dataset.productId || '',
          variantId: variant.dataset.variantId || '',
          grams: Number(variant.dataset.portionGrams) || 0
        }
      }));
    }

    return variant;
  }

  function makeFailureReadable(form) {
    const error = form.querySelector('[x-ref="error_message"]');
    if (!error) return;
    window.setTimeout(() => {
      error.textContent = 'Det ønskede antal er ikke længere tilgængeligt. Opdatér siden og prøv igen.';
    }, 0);
  }

  function createController(card) {
    const form = card.querySelector('[data-frozen-meals-form]');
    if (!form) return null;

    const abortController = new AbortController();
    const signal = abortController.signal;
    const radios = Array.from(form.querySelectorAll('[data-frozen-variant-radio]'));
    const input = form.querySelector('[data-frozen-quantity-input]');
    const minusButton = form.querySelector('[data-frozen-quantity-minus]');
    const plusButton = form.querySelector('[data-frozen-quantity-plus]');
    const addButton = form.querySelector('[data-frozen-add]');
    let requestPending = false;

    if (radios.length > 0 && !radios.some((radio) => radio.checked && !radio.disabled)) {
      const firstBuyable = radios.find((radio) => !radio.disabled && radio.dataset.buyable === 'true');
      if (firstBuyable) firstBuyable.checked = true;
    }

    radios.forEach((radio) => {
      radio.addEventListener('change', () => updateVariantState(card, form), { signal });

      const label = radio.nextElementSibling;
      if (label && label.hasAttribute('data-frozen-variant-label')) {
        label.addEventListener('click', (event) => {
          event.preventDefault();
          if (radio.disabled || radio.dataset.buyable !== 'true') return;
          radio.focus();
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, { signal });
      }
    });

    if (minusButton && input) {
      minusButton.addEventListener('click', () => {
        input.value = String((Number.parseInt(input.value, 10) || 1) - 1);
        updateQuantityControls(form, selectedVariant(form));
      }, { signal });
    }

    if (plusButton && input) {
      plusButton.addEventListener('click', () => {
        input.value = String((Number.parseInt(input.value, 10) || 1) + 1);
        updateQuantityControls(form, selectedVariant(form));
      }, { signal });
    }

    if (input) {
      ['change', 'blur'].forEach((eventName) => {
        input.addEventListener(eventName, () => updateQuantityControls(form, selectedVariant(form)), { signal });
      });
    }

    if (addButton) {
      addButton.addEventListener('click', (event) => {
        const variant = updateVariantState(card, form);
        if (!variant) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        requestPending = true;
      }, { capture: true, signal });
    }

    form.addEventListener('submit', (event) => {
      const variant = updateVariantState(card, form);
      if (!variant) {
        event.preventDefault();
        return;
      }
      requestPending = true;
    }, { capture: true, signal });

    document.addEventListener('eurus:product:add-failed', () => {
      if (!requestPending) return;
      requestPending = false;
      makeFailureReadable(form);
    }, { signal });

    document.addEventListener('eurus:product:added', () => {
      requestPending = false;
    }, { signal });

    updateVariantState(card, form);

    return {
      destroy() {
        abortController.abort();
      },
      sync() {
        return updateVariantState(card, form);
      }
    };
  }

  function initCard(card) {
    if (!card || controllers.has(card)) return;
    const controller = createController(card);
    if (controller) controllers.set(card, controller);
  }

  function destroyCard(card) {
    const controller = controllers.get(card);
    if (!controller) return;
    controller.destroy();
    controllers.delete(card);
  }

  function cardsWithin(root) {
    const cards = [];
    if (root && root.matches && root.matches('[data-frozen-meals-card]')) cards.push(root);
    if (root && root.querySelectorAll) {
      root.querySelectorAll('[data-frozen-meals-card]').forEach((card) => cards.push(card));
    }
    return cards;
  }

  function scan(root) {
    cardsWithin(root || document).forEach(initCard);
  }

  function syncAll(root) {
    cardsWithin(root || document).forEach((card) => {
      const controller = controllers.get(card);
      if (controller) {
        controller.sync();
      } else {
        initCard(card);
      }
    });
  }

  function sync(card) {
    const controller = controllers.get(card);
    return controller ? controller.sync() : null;
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    cardsWithin(event.target).forEach(destroyCard);
  });
  window.addEventListener('pageshow', () => syncAll());

  window.SimplyMealsFrozenMeals = Object.freeze({
    initCard,
    normalizeQuantity,
    scan,
    syncAll,
    sync,
    toInventory
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }
}());
